/**
 * Path builders for in-app navigation (`goto()` argument).
 *
 * These return path-only strings (no origin, no fragment). For
 * share-URL construction (clipboard, Telegram, navigator.share),
 * use the corresponding builder in `./share.ts`.
 *
 * Every builder carries `@dpi-visibility` (network-layer fingerprint risk
 * on partner-edge access logs) and `@secret-carrier` (which URL component
 * holds confidential data, if any). These tags are auditor-facing.
 *
 * Return type: `BuiltPath` (branded newtype). Only these builders call
 * `asBuiltPath` — callers receive a validated brand token, not a raw string.
 * This enforces the trust boundary: `RedirectAction.to` only accepts
 * `BuiltPath`, so only builder output can be used as a redirect target.
 */

import type { RoomId, PubkeyShort, BuiltPath } from './types';
import { asBuiltPath } from './types';

export interface CallPathOptions {
  /** Force audio-only mode via ?audio=1 query parameter. Server-visible. */
  audioOnly?: boolean;
}

/**
 * 1:1 video/audio call.
 * @dpi-visibility low (bare-root path, indistinguishable from random ID)
 * @secret-carrier none (path-only; share-URL fragment carries secret)
 */
export function call1to1Path(roomId: RoomId, opts: CallPathOptions = {}): BuiltPath {
  const base = `/${roomId}`;
  return asBuiltPath(opts.audioOnly ? `${base}?audio=1` : base);
}

/**
 * 1:1 call with pre-attached hash fragment (internal soft-reload only).
 * Caller owns hash format (including leading '#').
 * @dpi-visibility low
 * @secret-carrier fragment (caller-supplied; never sent to server)
 */
export function call1to1PathWithHash(roomId: RoomId, hash: string): BuiltPath {
  return asBuiltPath(`/${roomId}${hash}`);
}

/**
 * Group call (WebRTC mesh + SFU upgrade ≥3 peers).
 * @dpi-visibility medium ('/r/' prefix is distinct in access logs)
 * @secret-carrier none
 */
export function callGroupPath(roomId: RoomId): BuiltPath {
  return asBuiltPath(`/r/${roomId}`);
}

/**
 * Burner persistent chat (sealed ephemeral with fragment-carried key).
 * @dpi-visibility medium ('/c/' prefix is distinct)
 * @secret-carrier none (path-only; share-URL fragment carries key)
 */
export function burnerChatPath(roomId: RoomId): BuiltPath {
  return asBuiltPath(`/c/${roomId}`);
}

/**
 * Sealed 1:1 persistent SDK chat (Chat V7, feature-flagged).
 * @dpi-visibility medium
 * @secret-carrier none
 */
export function sealedChatPath(roomId: RoomId): BuiltPath {
  return asBuiltPath(`/m/${roomId}`);
}

/**
 * Sealed 1:1 chat at its FINAL url, `/{roomId}?hint=sealed` — where the legacy
 * `/m/{roomId}` shim (sealedChatPath) 308-redirects. Use this for new navigation
 * so shim usage (`redirect_hit` for `legacy-sealed-shim`) can reach zero.
 * @dpi-visibility low (bare-root path)
 * @secret-carrier none
 */
export function sealedChatDirectPath(roomId: RoomId): BuiltPath {
  return asBuiltPath(`/${roomId}?hint=sealed`);
}

/**
 * User profile page.
 * @dpi-visibility low
 * @secret-carrier none (path-only; pubkey-fragment is in share-URL form)
 * Phase 2 — see docs/ROADMAP.md §Phase 2 and docs/superpowers/plans/2026-05-10-contacts-mvp-plan-v2.md
 */
export function profilePath(pubkeyShort: PubkeyShort): BuiltPath {
  return asBuiltPath(`/u/${pubkeyShort}`);
}

/**
 * Contact detail page.
 * @dpi-visibility medium
 * @secret-carrier none
 * Phase 2 — see docs/ROADMAP.md §Phase 2 and docs/superpowers/plans/2026-05-09-contacts-mvp-plan.md
 */
export function contactPath(pubkeyShort: PubkeyShort): BuiltPath {
  return asBuiltPath(`/contacts/${pubkeyShort}`);
}

/**
 * Directory profile page for a claimed handle — the page a searcher lands on
 * after finding someone they have not added yet.
 *
 * Takes the CANONICAL handle, not raw user input. `classifyDirectoryInput`
 * ($lib/contacts/directory-input) is the single authority for that
 * normalization and this builder deliberately does not re-derive it: a second
 * handle regex here is exactly the four-way drift that module was created to
 * end. Passing a raw string produces a URL that resolves (the server
 * lowercases) but disagrees with the directory row it came from.
 *
 * @dpi-visibility medium ('/handle/' prefix is distinct, and the segment is a
 *   plaintext username — the most identifying path on the app)
 * @secret-carrier none
 */
export function handlePath(handle: string): BuiltPath {
  return asBuiltPath(`/handle/${encodeURIComponent(handle)}`);
}

/**
 * Directory profile page for a short-id. Same contract as `handlePath`:
 * the argument is the canonical (lowercased) form, and the server rejects
 * uppercase with 422 rather than resolving it.
 *
 * @dpi-visibility medium ('/short/' prefix is distinct)
 * @secret-carrier none
 */
export function shortIdPath(shortId: string): BuiltPath {
  return asBuiltPath(`/short/${encodeURIComponent(shortId)}`);
}
